import { isDefined, isEmpty } from '@sgme/fp';
import { Collapse } from '@sgme/ui';
import { AccordionSection, FormatSectionTitleFn } from 'components/common-form/sidePanel/AccordionSection';
import type { BackTestProps } from 'components/common-form/sidePanel/backTest/BackTest.connect';
import { BackTestError } from 'components/common-form/sidePanel/backTest/BackTestError';
import { ConnectedBackTestInputs } from 'components/common-form/sidePanel/backTest/BackTestInputs.connect';
import { PeriodSerie } from 'components/common-form/sidePanel/backTest/PeriodSerie';
import { useRunTestCb } from 'components/common-form/sidePanel/backTest/shared';
import { Statistics } from 'components/common-form/sidePanel/backTest/Statistics';
import { TimeSerie } from 'components/common-form/sidePanel/backTest/TimeSerie';
import { ValidationErrorComponent } from 'components/common-form/sidePanel/status/validationErrors/ValidationErrors.status';
import { useIsInternalUser } from 'hooks/useIsInternalUser';
import { useCallback, useMemo, useState } from 'react';
import { Modal, ModalBody, ModalHeader, ModalTitle } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { BackTestErrorModel, BackTestModel, Serie } from 'state/backTest/backTest.model';
import type { AppState } from 'state/state.model';
import { getSplabPricingUrl } from 'state/user';

type ModalSerie = keyof Pick<BackTestModel, 'cumulatedAutocallProbSeries' | 'irrEvolutionSeries'>;

function trackDownload() {
  logAnalytics.trackEvent('Backtest', 'Download results');
}

function shouldDisplaySerie<T>(values: readonly Serie<T>[]) {
  return isEmpty(values) || values.some(({ data }) => !isEmpty(data));
}

type AccordionKey = 'statistics' | 'cumulatedAutocallProbSeries' | 'irrEvolutionSeries';

const accordionAllKeys: AccordionKey[] = ['statistics', 'cumulatedAutocallProbSeries', 'irrEvolutionSeries'];

export function BackTest({
  quoteIds,
  canRunBackTest,
  irrEvolutionSeries,
  cumulatedAutocallProbSeries,
  runTest,
  cancelTest,
  status,
  statistics,
  requestedQuoteIds,
  requestedQuoteLabels,
  resetTest,
  precision,
  backTestId,
  setExpandedSection,
  expandedData,
  hasError,
  hasResult,
  isRunning,
  retryTest,
  hasErrorForInitialQuoteId,
  hasErrorForSecondQuoteId,
  errors,
  maturitiesValidationError,
  hasRunBackTest,
  formId,
}: BackTestProps) {
  const [openInModalSerie, setOpenInModalSerie] = useState<ModalSerie>();
  const closeModal = () => setOpenInModalSerie(undefined);
  const isInternalUser = useIsInternalUser();
  const hasStatistics = Object.keys(statistics).length > 0;
  const showSpinners =
    status === 'RUN_BACK_TEST_LOADING' || status === 'GET_BACK_TEST_LOADING' || status === 'RUN_BACK_TEST_SUCCESS';
  const isResultReady = status === 'GET_BACK_TEST_SUCCESS';
  const runTestCb = useRunTestCb(runTest);

  const cancelTestCb = useCallback(() => {
    logAnalytics.trackEvent('Backtest', 'Cancel backtest');
    cancelTest();
  }, [cancelTest]);
  const splabPricingUrl = useSelector((state: AppState) => getSplabPricingUrl(state));

  const downloadLink = `${splabPricingUrl}download/${backTestId}/backtest.xlsx`;

  const timeSerie = useMemo(
    () => (
      <TimeSerie
        xAxisKey="backTest.irrEvolutionSeries.xAxis.title"
        yAxisKey="backTest.irrEvolutionSeries.yAxis.title"
        titleKey="backTest.irrEvolutionSeries"
        backTestId={backTestId!}
        series={irrEvolutionSeries}
        quoteIds={requestedQuoteIds}
        quoteLabels={requestedQuoteLabels}
        hasErrorForInitialQuoteId={hasErrorForInitialQuoteId}
        hasErrorForSecondQuoteId={hasErrorForSecondQuoteId}
        precision={precision}
        hasCopyToClipBoard
      />
    ),
    [
      irrEvolutionSeries,
      requestedQuoteIds,
      requestedQuoteLabels,
      precision,
      backTestId,
      hasErrorForInitialQuoteId,
      hasErrorForSecondQuoteId,
    ],
  );
  const periodSerie = useMemo(
    () => (
      <PeriodSerie
        xAxisKey="backTest.cumulatedAutocallProbSeries.xAxis.title"
        yAxisKey="backTest.cumulatedAutocallProbSeries.yAxis.title"
        titleKey="backTest.cumulatedAutocallProbSeries"
        backTestId={backTestId!}
        series={cumulatedAutocallProbSeries}
        quoteIds={requestedQuoteIds}
        quoteLabels={requestedQuoteLabels}
        precision={precision}
        hasErrorForInitialQuoteId={hasErrorForInitialQuoteId}
        hasErrorForSecondQuoteId={hasErrorForSecondQuoteId}
        hasCopyToClipBoard
        showMarkers
      />
    ),
    [
      cumulatedAutocallProbSeries,
      requestedQuoteIds,
      precision,
      backTestId,
      requestedQuoteLabels,
      hasErrorForInitialQuoteId,
      hasErrorForSecondQuoteId,
    ],
  );

  const onSelectAccordionItem = (itemKey: AccordionKey) => {
    const isExpanded = hasResult && expandedData[itemKey];
    setExpandedSection(itemKey, !isExpanded);
  };

  const selected: AccordionKey[] = [];
  if (hasResult) {
    accordionAllKeys.forEach(key => {
      if (expandedData[key]) {
        selected.push(key);
      }
    });
  }

  const getTitle: FormatSectionTitleFn<AccordionKey> = (section, formatMsg) => formatMsg(`backTest.${section}`);

  return (
    <div className="overflow-y-auto d-flex flex-column h-100  px-3" data-e2e="back-test">
      <label className="form-label d-flex w-100 my-2">
        <FormattedMessage id="backTest.quoteId" />
      </label>
      <ConnectedBackTestInputs formId={formId} />
      {maturitiesValidationError !== undefined && (
        <ValidationErrorComponent<BackTestErrorModel> errorPrefix="" validationErrors={[maturitiesValidationError]} />
      )}
      <BackTestError retryTest={retryTest} hasError={hasError} errors={errors} requestedQuoteIds={requestedQuoteIds} />
      <div className="d-flex flex-wrap h-100 w-100 overflow-y-auto ">
        {!hasError && hasRunBackTest && (
          <Collapse
            icon="end"
            size="sm"
            fill="fill"
            className="flex-grow-1"
            activeKey={selected}
            onSelect={itemKey => onSelectAccordionItem(itemKey as AccordionKey)}
          >
            {hasStatistics && (
              <AccordionSection<AccordionKey>
                buttonClassName="btn-link"
                section="statistics"
                showSpinners={showSpinners}
                disabled={!isResultReady}
                getTitle={getTitle}
              >
                <Statistics
                  quoteLabels={requestedQuoteLabels}
                  statistics={statistics}
                  quoteIds={requestedQuoteIds}
                  className="px-2"
                  precision={precision}
                  hasErrorForInitialQuoteId={hasErrorForInitialQuoteId}
                  hasErrorForSecondQuoteId={hasErrorForSecondQuoteId}
                />
              </AccordionSection>
            )}
            {shouldDisplaySerie(irrEvolutionSeries) && (
              <AccordionSection<AccordionKey>
                buttonClassName="btn-link"
                section="irrEvolutionSeries"
                showSpinners={showSpinners}
                disabled={!isResultReady}
                openModal={() => setOpenInModalSerie('irrEvolutionSeries')}
                getTitle={getTitle}
              >
                {timeSerie}
              </AccordionSection>
            )}
            {shouldDisplaySerie(cumulatedAutocallProbSeries) && (
              <AccordionSection<AccordionKey>
                buttonClassName="btn-link"
                section="cumulatedAutocallProbSeries"
                showSpinners={showSpinners}
                disabled={!isResultReady}
                openModal={() => setOpenInModalSerie('cumulatedAutocallProbSeries')}
                getTitle={getTitle}
              >
                {periodSerie}
              </AccordionSection>
            )}
          </Collapse>
        )}
      </div>
      <div className="d-flex py-3 w-100 justify-content-around">
        {hasResult || hasError ? (
          <>
            <button className="btn btn-primary col me-1" onClick={resetTest}>
              <FormattedMessage id={hasResult ? 'backTest.reset' : 'ok'} />
            </button>
            {hasResult && isInternalUser && (
              <a
                className="btn btn-secondary btn-icon-end ms-1 col-6"
                download={backTestId ? backTestId + '.xlsx' : true}
                href={downloadLink}
                onClick={trackDownload}
                target="_blank"
              >
                <FormattedMessage id="backTest.exportResult" />
                <em className="icon icon-sm">download</em>
              </a>
            )}
          </>
        ) : isRunning && isDefined(backTestId) ? (
          <button className="btn btn-danger col me-1" disabled={!canRunBackTest} onClick={cancelTestCb}>
            <FormattedMessage id="backTest.cancel" />
          </button>
        ) : (
          <button
            className="btn btn-primary col me-1"
            disabled={!canRunBackTest || isRunning}
            onClick={() => runTestCb(quoteIds.secondQuoteId ? 'Multiple' : 'Single')}
          >
            <FormattedMessage id="backTest.run" />
          </button>
        )}
      </div>
      <Modal show={openInModalSerie !== undefined} onHide={closeModal} size="xl">
        {openInModalSerie !== undefined && (
          <>
            <ModalHeader onToggle={closeModal} closeButton>
              <ModalTitle as="h5">
                <FormattedMessage id={`backTest.${openInModalSerie}`} />
              </ModalTitle>
            </ModalHeader>
            <ModalBody>{openInModalSerie === 'irrEvolutionSeries' ? timeSerie : periodSerie}</ModalBody>
          </>
        )}
      </Modal>
    </div>
  );
}
